import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { StorageService } from '../services/storage.service';
import { SessionDataService } from '../services/session-data.service';
import { AuthService } from '../services/auth.service';
import { Location } from '@angular/common';
import { RefreshTokenResponse } from '../models/refresh-token-response.interface';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  readonly registerUrl = `${environment.baseUrl}/login`;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  constructor(
    private storageService: StorageService,
    private sessionDataService: SessionDataService,
    private authService: AuthService,
    private location: Location
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const accessToken = this.storageService.getItem('access-token');
    if (accessToken && request.url.search('refresh-token') === -1) {
      request = request.clone({
        setHeaders: {
          authorization: `Bearer ${accessToken}`,
        },
      });
    }
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          return this.handle401Error(request, next);
        }
        return throwError(() => err);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    const refreshToken = this.storageService.getItem('refresh-token')!;
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      return this.authService.refreshToken(refreshToken).pipe(
        switchMap((data: RefreshTokenResponse) => {
          this.sessionDataService.setSessionData({
            accessToken: data.token,
            refreshToken: data.refreshToken,
          });
          const newToken = data.token;
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
          });
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          return next.handle(request);
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.storageService.clear();
          window.location.href = this.registerUrl;
          this.location.replaceState('dashboard');
          window.location.reload();
          return throwError(() => err);
        })
      );
    }
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token: string) => {
        request = request.clone({
          setHeaders: {
            authorization: `Bearer ${token}`,
          },
        });
        return next.handle(request);
      })
    );
  }
}
